import React, { useEffect, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Check, Sparkles, Users } from "lucide-react";
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { useLayoutV2 } from "@/hooks/useLayoutV2";
import { useAuth } from "@/context/AuthContext";
import { isDirectie } from "@/lib/rollen";

const fmtDag = (iso) => (iso ? `${iso.slice(8, 10)}-${iso.slice(5, 7)}-${iso.slice(0, 4)}` : "");

/* Schakelaar in de zijbalk: alleen zichtbaar voor wie mag schakelen (directie/testers; na aanzetten iedereen tot terug_tot). */
export function LayoutV2Schakelaar() {
  const v2 = useLayoutV2();
  const { user } = useAuth();
  const [testersOpen, setTestersOpen] = useState(false);
  const [bezig, setBezig] = useState(false);
  if (!v2.mag_schakelen && !isDirectie(user)) return null;
  const aan = v2.actief;
  const wissel = async (naarAan) => {
    setBezig(true);
    try { await v2.zet(v2.flag_aan ? (naarAan ? null : "oud") : (naarAan ? "nieuw" : null)); toast.success(naarAan ? "Nieuwe opmaak staat aan voor jou" : "Oude opmaak staat weer aan voor jou"); }
    catch (e) { toast.error(formatErr(e?.response?.data?.detail || e?.message)); }
    setBezig(false);
  };
  return (
    <div className="px-3 py-1.5 space-y-1" data-testid="layout-v2-schakelaar">
      {v2.mag_schakelen && (
        <label className="flex items-center justify-between gap-2 text-sm text-slate-600 cursor-pointer">
          <span className="inline-flex items-center gap-2 min-w-0"><Sparkles className="h-4 w-4 text-sky-600 shrink-0" /><span className="truncate">{v2.flag_aan ? "Oude opmaak" : "Nieuwe opmaak proberen"}</span></span>
          <Switch checked={v2.flag_aan ? !aan : aan} onCheckedChange={(c) => wissel(v2.flag_aan ? !c : c)} disabled={bezig} data-testid="layout-v2-switch-toggle" />
        </label>
      )}
      {v2.flag_aan && v2.terug_tot && <div className="text-[10px] text-slate-400">terug naar oud mogelijk tot {fmtDag(v2.terug_tot)}</div>}
      {isDirectie(user) && <button onClick={() => setTestersOpen(true)} className="text-[11px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-1" data-testid="layout-v2-testers-knop"><Users className="h-3 w-3" /> Testers nieuwe opmaak…</button>}
      {testersOpen && <TestersDialog onClose={() => setTestersOpen(false)} />}
    </div>
  );
}

/* Indicator boven de pagina als de nieuwe opmaak als proef (flag uit) actief is. */
export function LayoutV2Indicator() {
  const v2 = useLayoutV2();
  if (!v2.actief || v2.flag_aan) return null;
  return <span className="h-5 px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-[10px] font-semibold tracking-wide inline-flex items-center gap-1.5" data-testid="layout-v2-proef-indicator"><Sparkles className="h-3 w-3" /> Nieuwe opmaak (proef)</span>;
}

function TestersDialog({ onClose }) {
  const [zoek, setZoek] = useState("");
  const [data, setData] = useState(null);
  const laad = () => api.get("/layout-v2/testers", { params: { zoek } }).then((r) => setData(r.data)).catch((e) => toast.error(formatErr(e?.response?.data?.detail)));
  useEffect(() => { const t = setTimeout(laad, 250); return () => clearTimeout(t); }, [zoek]); // eslint-disable-line
  const zet = async (u, tester) => {
    try { await api.put(`/layout-v2/testers/${u.id}`, { tester }); toast.success(`${u.name}: schakelaar ${tester ? "beschikbaar" : "weggehaald"}`); laad(); }
    catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="layout-v2-testers-dialog">
        <DialogHeader><DialogTitle>Testers nieuwe opmaak</DialogTitle><DialogDescription>Aangewezen medewerkers krijgen in de zijbalk de schakelaar "Nieuwe opmaak proberen". Directie heeft hem altijd. Na het aanzetten van de flag voor iedereen blijft terugschakelen {data?.terug_dagen ?? 14} dagen mogelijk.</DialogDescription></DialogHeader>
        <Input value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder="Zoek op naam of e-mail…" data-testid="layout-v2-testers-zoek" />
        <div className="max-h-[50vh] overflow-y-auto divide-y text-sm">
          {(data?.testers || []).map((u) => (
            <label key={u.id} className="flex items-center gap-3 py-1.5 cursor-pointer" data-testid={`layout-v2-tester-${u.id}`}>
              <Checkbox checked={u.tester} disabled={u.directie} onCheckedChange={(c) => zet(u, !!c)} />
              <span className="flex-1 min-w-0"><span className="font-medium">{u.name}</span> <span className="text-slate-500">· {u.role}</span>{u.voorkeur && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200">{u.voorkeur === "nieuw" ? "probeert nieuw" : "oud gekozen"}</span>}</span>
              {u.directie && <span className="text-[10px] text-slate-400">directie</span>}
            </label>
          ))}
          {data && data.testers.length === 0 && <div className="py-4 text-center text-slate-500">Geen medewerkers gevonden.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}


/* Menu-items voor het account-menu in de bovenbalk (TopBar): schakelaar + testersbeheer. Alleen zichtbaar voor wie mag schakelen / directie. */
export function LayoutV2MenuItems() {
  const v2 = useLayoutV2();
  const { user } = useAuth();
  if (!v2.mag_schakelen && !isDirectie(user)) return null;
  const aan = v2.actief;
  const wissel = async () => {
    try { await v2.zet(v2.flag_aan ? (aan ? "oud" : null) : (aan ? null : "nieuw")); toast.success(aan ? "Oude opmaak staat weer aan voor jou" : "Nieuwe opmaak staat aan voor jou"); }
    catch (e) { toast.error(formatErr(e?.response?.data?.detail || e?.message)); }
  };
  return (
    <>
      <DropdownMenuSeparator />
      {v2.mag_schakelen && (
        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); wissel(); }} data-testid="layout-v2-switch-toggle" className="gap-2">
          <Sparkles className="h-4 w-4 text-sky-600" /><span className="flex-1">{v2.flag_aan ? "Oude opmaak gebruiken" : "Nieuwe opmaak proberen"}</span>
          <span className={`h-4 w-4 rounded border inline-flex items-center justify-center ${(v2.flag_aan ? !aan : aan) ? "bg-sky-600 border-sky-600 text-white" : "border-slate-300"}`} data-testid="layout-v2-switch-stand" data-aan={String(aan)}>{(v2.flag_aan ? !aan : aan) && <Check className="h-3 w-3" />}</span>
        </DropdownMenuItem>
      )}
      {v2.flag_aan && v2.terug_tot && <div className="px-2 pb-1 text-[10px] text-slate-400">terug naar oud mogelijk tot {fmtDag(v2.terug_tot)}</div>}
      {isDirectie(user) && <DropdownMenuItem onSelect={() => window.dispatchEvent(new CustomEvent("bpt:layout-v2-testers"))} data-testid="layout-v2-testers-knop" className="gap-2"><Users className="h-4 w-4 text-slate-500" /> Testers nieuwe opmaak…</DropdownMenuItem>}
    </>
  );
}

/* Host voor de testers-dialog buiten het (sluitende) account-menu — één keer in de TopBar plaatsen. */
export function LayoutV2TestersHost() {
  const [open, setOpen] = useState(false);
  useEffect(() => { const f = () => setOpen(true); window.addEventListener("bpt:layout-v2-testers", f); return () => window.removeEventListener("bpt:layout-v2-testers", f); }, []);
  return open ? <TestersDialog onClose={() => setOpen(false)} /> : null;
}
